import { Box, Paper, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../../shared/components/PageHeader';
import { resources } from '../../core/services/resources';
import { useTenant } from '../../core/contexts/TenantContext';

interface KpiCardProps {
  label: string;
  value: number | string;
  hint?: string;
}

function KpiCard({ label, value, hint }: KpiCardProps) {
  return (
    <Paper sx={{ p: 3, minWidth: 200, flex: 1 }}>
      <Typography variant="overline" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h4" fontWeight={600}>
        {value}
      </Typography>
      {hint && (
        <Typography variant="caption" color="text.secondary">
          {hint}
        </Typography>
      )}
    </Paper>
  );
}

/**
 * Cheap dashboard: every card just calls `list({ pageSize: 1 })` and reads
 * the `page.total` count. Good enough for an initial overview without
 * adding a dedicated `/stats` endpoint.
 */
export function DashboardPage() {
  const { activeTenantName } = useTenant();
  const projects = useQuery({
    queryKey: ['projects', { count: true }],
    queryFn: () => resources.projects.list({ pageSize: 1 }),
  });
  const items = useQuery({
    queryKey: ['items', { count: true }],
    queryFn: () => resources.items.list({ pageSize: 1 }),
  });
  const tags = useQuery({
    queryKey: ['tags', { count: true }],
    queryFn: () => resources.tags.list({ pageSize: 1 }),
  });
  const users = useQuery({
    queryKey: ['users', { count: true }],
    queryFn: () => resources.users.list({ pageSize: 1 }),
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <PageHeader
        title="Dashboard"
        description={
          activeTenantName
            ? `Overview for tenant "${activeTenantName}".`
            : 'Overview for the active tenant.'
        }
      />
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
        <KpiCard label="Projects" value={projects.data?.page.total ?? '—'} hint="Tenant-scoped" />
        <KpiCard label="Items" value={items.data?.page.total ?? '—'} hint="Across all projects" />
        <KpiCard label="Tags" value={tags.data?.page.total ?? '—'} hint="Global catalog" />
        <KpiCard label="Users" value={users.data?.page.total ?? '—'} hint="Platform total" />
      </Stack>
    </Box>
  );
}
